import React from 'react';
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import { motion } from 'framer-motion';
// 레거시 컴포넌트용 타입 정의 (통합 데이터와 독립적)
interface SubModel {
  modelNumber: string;
  displayName: string;
  storage?: string;
  connectivity?: string;
  color?: string;
}
import StorageIcon from '@mui/icons-material/Storage';
import WifiIcon from '@mui/icons-material/Wifi';
import CellularIcon from '@mui/icons-material/CellTower';
import UsbIcon from '@mui/icons-material/Usb';

interface SubModelCardProps {
  subModel: SubModel;
  selected: boolean;
  onClick: () => void;
  index: number;
}



const getConnectivityIcon = (connectivity: string) => {
  if (connectivity?.includes('Cellular')) return <CellularIcon sx={{ fontSize: 16 }} />;
  if (connectivity?.includes('Wi-Fi')) return <WifiIcon sx={{ fontSize: 16 }} />;
  if (connectivity?.includes('Lightning') || connectivity?.includes('USB-C')) return <UsbIcon sx={{ fontSize: 16 }} />;
  return <WifiIcon sx={{ fontSize: 16 }} />;
};

export default function SubModelCard({ subModel, selected, onClick, index }: SubModelCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Card
        onClick={onClick}
        sx={{
          cursor: 'pointer',
          height: '100%',
          border: selected ? '2px solid #6366f1' : '1px solid rgba(0,0,0,0.08)',
          borderRadius: 4,
          backgroundColor: selected ? 'rgba(99, 102, 241, 0.04)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: selected 
              ? '0 20px 40px rgba(99, 102, 241, 0.15), 0 8px 16px rgba(99, 102, 241, 0.1)'
              : '0 20px 40px rgba(0,0,0,0.08), 0 8px 16px rgba(0,0,0,0.04)',
            backgroundColor: selected ? 'rgba(99, 102, 241, 0.08)' : 'rgba(255, 255, 255, 1)',
            borderColor: selected ? '#6366f1' : 'rgba(99, 102, 241, 0.2)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: selected 
              ? 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)'
              : 'transparent',
            transition: 'all 0.3s ease',
          }
        }}
      >
        <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* 모델 번호 배지 */}
          <Box sx={{ mb: 2 }}>
            <Chip
              label={subModel.modelNumber}
              size="small"
              sx={{
                backgroundColor: selected ? '#6366f1' : 'rgba(0,0,0,0.06)',
                color: selected ? 'white' : '#374151',
                fontSize: '0.75rem',
                fontWeight: 600,
                fontFamily: 'monospace',
                letterSpacing: '0.5px',
                height: 24,
                '& .MuiChip-label': {
                  px: 1.5,
                }
              }}
            />
          </Box>

          {/* 제품명 */}
          <Typography 
            variant="h6" 
            sx={{ 
              fontSize: '1rem', 
              fontWeight: 700,
              color: selected ? '#6366f1' : '#1f2937',
              lineHeight: 1.4,
              mb: 2,
              flex: 1,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {subModel.displayName}
          </Typography>
          
          {/* 세부 정보 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* 저장 용량 */}
            {subModel.storage && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StorageIcon sx={{ 
                  fontSize: 16, 
                  color: selected ? '#6366f1' : '#6b7280' 
                }} />
                <Typography variant="body2" sx={{ 
                  color: selected ? '#6366f1' : '#374151',
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}>
                  {subModel.storage}
                </Typography>
              </Box>
            )}
            
            {/* 연결성 */}
            {subModel.connectivity && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getConnectivityIcon(subModel.connectivity)}
                <Typography variant="body2" sx={{ 
                  color: selected ? '#6366f1' : '#374151',
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}>
                  {subModel.connectivity}
                </Typography>
              </Box>
            )}
            

          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
} 